import type { StyleSpecification } from "maplibre-gl";
import { parseColor, solidColor } from "./colors";
import { getMainColor, MainColor, setMainColor } from "./main";
import { differsByZoom, getZoomColors, setZoomColor, ZoomSide } from "./zoom";

export const THEME_FORMAT = "atlas-theme/1";

export type Theme = {
  format: string;
  colors: { [mainColorId: string]: string };
};

export type ParseResult =
  | { ok: true; colors: { [mainColorId: string]: string } }
  | { ok: false; errors: string[] };

// Longer than any real answer; a guard against pasting the wrong thing.
const MAX_REPLY_LENGTH = 20000;

type Entry = {
  key: string;
  title: string;
  read(style: StyleSpecification): string | undefined;
  write<S extends StyleSpecification>(style: S, color: string): S;
};

const SIDE_TITLES: { [side in ZoomSide]: string } = {
  out: "zoomed out",
  in: "zoomed in",
};

// One entry per main colour; a family whose far and close colours differ
// gets one entry per end instead.
function themeEntries(style: StyleSpecification, mains: MainColor[]): Entry[] {
  return mains.flatMap((main): Entry[] => {
    if (!differsByZoom(style, main)) {
      return [
        {
          key: main.id,
          title: main.title,
          read: (current) => getMainColor(current, main),
          write: (current, color) => setMainColor(current, main, color),
        },
      ];
    }
    return (["out", "in"] as ZoomSide[]).map((side) => ({
      key: `${main.id}_zoomed_${side}`,
      title: `${main.title}, ${SIDE_TITLES[side]}`,
      read: (current) => getZoomColors(current, main)?.[side],
      write: (current, color) => setZoomColor(current, main, side, color),
    }));
  });
}

// The small colour block users hand to an AI: one entry per main colour, as
// a solid colour. No layers, no tokens.
export function exportTheme(style: StyleSpecification, mains: MainColor[]): Theme {
  const colors: { [key: string]: string } = {};
  themeEntries(style, mains).forEach((entry) => {
    const color = entry.read(style);
    if (color) colors[entry.key] = solidColor(color);
  });
  return { format: THEME_FORMAT, colors };
}

export function buildAiPrompt(
  style: StyleSpecification,
  mains: MainColor[]
): string {
  const legend = themeEntries(style, mains)
    .map((entry) => `- ${entry.key}: ${entry.title}`)
    .join("\n");
  const block = JSON.stringify(exportTheme(style, mains), null, 2);
  return [
    "You are recolouring a map. The JSON block at the end lists the map's main colours.",
    "Change the colour values to match this request:",
    "",
    "<<< DESCRIBE THE LOOK YOU WANT HERE >>>",
    "",
    "Rules:",
    "- Reply with only the JSON block, inside a ```json code fence.",
    "- Keep every key exactly as it is. Do not add, remove or rename keys.",
    "- Every value must be a solid hex colour such as #1a2b3c, without transparency.",
    "- Keep label text readable against land, water and roads.",
    "- Keep land, water and roads easy to tell apart.",
    "- Related shades (outlines, tunnels, zoom levels) follow each colour automatically.",
    "",
    "What each key colours:",
    legend,
    "",
    "```json",
    block,
    "```",
  ].join("\n");
}

function extractJson(text: string): string | undefined {
  const fenced = text.match(/```(?:json)?\s*([\s\S]*?)```/i);
  const candidate = fenced ? fenced[1] : text;
  const start = candidate.indexOf("{");
  const end = candidate.lastIndexOf("}");
  if (start === -1 || end <= start) return undefined;
  return candidate.slice(start, end + 1);
}

// Reads an AI reply. Accepts the full block or a bare { key: colour } object,
// with or without a code fence or surrounding chatter. Never throws. With
// the style, the keys of families split by zoom are known too; a family's
// own key is always accepted and moves the whole family.
export function parseTheme(
  text: string,
  mains: MainColor[],
  style?: StyleSpecification
): ParseResult {
  if (text.length > MAX_REPLY_LENGTH) {
    return { ok: false, errors: ["The pasted text is too long."] };
  }

  const json = extractJson(text);
  if (!json) {
    return { ok: false, errors: ["No colour block found in the pasted text."] };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch (err) {
    return { ok: false, errors: ["The colour block is not valid JSON."] };
  }

  const wrapped = (parsed as { colors?: unknown })?.colors;
  const entries = wrapped !== undefined ? wrapped : parsed;
  if (!entries || typeof entries !== "object" || Array.isArray(entries)) {
    return { ok: false, errors: ["The colour block has an unexpected shape."] };
  }

  const known = new Set([
    ...mains.map((main) => main.id),
    ...(style ? themeEntries(style, mains).map((entry) => entry.key) : []),
  ]);
  const colors: { [mainColorId: string]: string } = {};
  const errors: string[] = [];

  Object.entries(entries as { [key: string]: unknown }).forEach(
    ([key, value]) => {
      if (wrapped === undefined && key === "format") return;
      if (!known.has(key)) {
        errors.push(`Unknown colour name: ${key}`);
        return;
      }
      const color = parseColor(value);
      if (!color) {
        errors.push(`Not a valid colour for ${key}: ${JSON.stringify(value)}`);
      } else if (color.alpha() < 1) {
        // Main colours change hue, saturation and lightness only.
        errors.push(`Not a solid colour for ${key}: ${JSON.stringify(value)}`);
      } else {
        colors[key] = solidColor(value as string);
      }
    }
  );

  if (errors.length > 0) return { ok: false, errors };
  if (Object.keys(colors).length === 0) {
    return { ok: false, errors: ["The colour block contains no colours."] };
  }
  return { ok: true, colors };
}

// Families do not overlap, so the order of application does not matter.
// A family's own key is applied before the keys of its two ends.
export function applyTheme<S extends StyleSpecification>(
  style: S,
  mains: MainColor[],
  colors: { [key: string]: string }
): S {
  const whole = mains.reduce(
    (next, main) =>
      colors[main.id] ? setMainColor(next, main, colors[main.id]) : next,
    style
  );
  return themeEntries(style, mains)
    .filter((entry) => entry.key !== entry.key.replace(/_zoomed_(out|in)$/, ""))
    .reduce(
      (next, entry) => (colors[entry.key] ? entry.write(next, colors[entry.key]) : next),
      whole
    );
}
